import { createSlice } from "@reduxjs/toolkit"
import initialState from "./initialState"
import {
  validateComments,
  validateCustom,
  validateEmail,
  validateFullname,
  validatePhone,
} from "@/components/calendar/utils/fieldValidations"
import {
  cancelVisit,
  confirmVisit,
  getDayHours,
  getMonthAvailableDays,
} from "./services"
import { parseFirstAvailableDate } from "@/components/calendar/utils/dates"
import parseParticipants from "@/components/calendar/utils/parseParticipants"

const visitStatusIds = {
  confirmed: 1,
  canceled: 2,
  pending: 3,
  past: 4,
  finished: 5,
}

export const CalendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    setCalendarData(state, action) {
      const { key, subkey, value } = action.payload
    
      if (subkey) {
        state[key] = state[key] || {}
        state[key][subkey] = value
      } else {
        state[key] = value
      }
    },
    setCalendarFormData(state, action) {
      const { key, subkey, value } = action.payload

      state.form[key][subkey] = value
      
      if (subkey === 'value') {
        const validations = {
          fullname: validateFullname,
          email: validateEmail,
          phone: validatePhone,
          comment: validateComments,
        }
        const validateField = validations[key] || validateCustom
        const validatedField = validateField(value, state.form[key].required)

        state.form[key].error = !validatedField.valid
        state.form[key].errorKey = validatedField.errorKey
      }
    },
    setCalendarFormFocus(state, action) {
      const { key, focused } = action.payload
      state.form[key].focused = focused
    },
    setPreLoadedCalendarFormData(state, action) {
      const { fullname, email, phone } = action.payload

      if (fullname) {
        state.form.fullname = {
          ...state.form.fullname,
          value: fullname,
          restrictedValue: fullname,
        }
      }

      if (email) {
        state.form.email = {
          ...state.form.email,
          value: email,
          restrictedValue: email,
        }
      }

      if (phone) {
        state.form.phone = {
          ...state.form.phone,
          value: phone,
          restrictedValue: phone,
        }
      }
    },
    setInitialCalendarData(state, action) {
      const {
        property_id,
        time_zone_used_in_time_slots,
        property_pic,
        company_pic,
        duration,
        available_days,
        direction,
        time_slots_day_selected,
        status,
        string_location,
        branch,
        contact_info,
        participants_info,
        custom_fields,
        next_available_date,
        host,
      } = action.payload

      const openStatus = 1
      const isOpen = status === openStatus

      const property = {
        id: property_id,
        location: string_location,
        name: direction,
        thumbnail: property_pic,
        gmt: time_zone_used_in_time_slots,
      }
      const schedule = {
        hours: time_slots_day_selected,
        available_days: available_days
      }
      const company = {
        thumbnail: company_pic,
      }
      const participants = participants_info
      const contactInfo = contact_info
      const customFields = custom_fields
      const defaultStatus = visitStatusIds.pending

      const availableDays = schedule.available_days
      const firstAvailableDate = parseFirstAvailableDate(next_available_date)
      const availableHours = firstAvailableDate ? schedule.hours : []
      const firstAvailableHour = firstAvailableDate ? availableHours[0] : null
      const parsedParticipants = parseParticipants(participants)

      const mappedFixedFormValues = {
        fullname: {
          needed: true,
          required: true,
        },
        email: {
          needed: contactInfo.email_needed,
          required: contactInfo.email_required,
        },
        phone: {
          needed: contactInfo.phone_needed,
          required: contactInfo.phone_required,
        },
        comment: {
          needed: contactInfo.comment_needed,
          required: contactInfo.comment_required,
        }
      }

      const parsedFixedFields = Object.fromEntries(
        Object.entries(state.form).map(([key, values]) => {
          const updatedValues = {...values, ...mappedFixedFormValues[key]}
          return [key, updatedValues]
        })
      )
      
      const parsedCustomFields = Object.fromEntries(
        customFields
          .map((customField) => [
            customField.custom_field_name,
            {
              required: customField.custom_fields_required,
              needed: customField.custom_field_needed,
              value: '',
              error: false,
              focused: false,
              restrictedValue: '',
              errorKey: '',
              isCustom: true,
            }
          ])
      );

      state.visit = {
        ...initialState.visit,
        date: firstAvailableDate,
        hour: firstAvailableHour,
        status: status ?? defaultStatus,
        visitDurationInMinutes: duration,
      }

      state.schedule = {
        availableDays,
        availableHours,
        initializing: false,
        loadingHours: false,
        loadingMonth: false,
        isMounted: true,
      }

      state.form = {
        ...parsedFixedFields,
        ...parsedCustomFields
      }

      state.step = initialState.step
      state.participants = parsedParticipants
      state.host = host
      state.property = property
      state.company = company
      state.branch = branch
      state.isOpen = isOpen
    },
    setInitialVisitData(state, action) {
      const { visit, property, company, participants, host } = action.payload
      const {
        id,
        status,
        visit_duration,
        date,
        hour,
        timezone,
        when,
        google_calendar_url,
        outlook_calendar_url
      } = visit

      const defaultStatus = visitStatusIds.pending
      const parsedParticipants = parseParticipants(participants)

      state.visit = {
        ...state.visit,
        id,
        date,
        hour,
        when,
        googleCalendarUrl: google_calendar_url,
        outlookCalendarUrl: outlook_calendar_url,
        timezone: { id: 1, value: timezone },
        status: status ?? defaultStatus,
        visitDurationInMinutes: visit_duration,
        initializing: false,
        confirming: false,
      }

      state.participants = parsedParticipants
      state.host = host
      state.property = property
      state.company = company
    },
    setKeepLoading(state) {
      state.schedule.initializing = true
    }
  },
    extraReducers: (builder) => {
      builder // handle day hours
        .addCase(getDayHours.pending, (state) => {
          state.schedule.loadingHours = true
        })
        .addCase(getDayHours.fulfilled, (state, action) => {
          const { time_slots_day_selected } = action.payload

          state.schedule.availableHours = time_slots_day_selected
          state.visit.hour = time_slots_day_selected[0]
          state.schedule.loadingHours = false
        })
        .addCase(getDayHours.rejected, (state) => {
          state.schedule.loadingHours = false
          console.log('Error getting available day hours!')
        });
      builder // handle month avaible days
        .addCase(getMonthAvailableDays.pending, (state) => {
          state.schedule.loadingMonth = true
          // state.schedule.loadingHours = true
        })
        .addCase(getMonthAvailableDays.fulfilled, (state, action) => {
          const { available_days } = action.payload

          state.schedule.availableDays = available_days
          state.schedule.loadingMonth = false
          // state.schedule.loadingHours = false
        })
        .addCase(getMonthAvailableDays.rejected, (state) => {
          state.schedule.loadingMonth = false
          // state.schedule.loadingHours = false
          console.log('Error getting month available days!')
        });
      builder // confirm visit
        .addCase(confirmVisit.pending, (state) => {
          state.visit.confirming = true
        })
        .addCase(confirmVisit.fulfilled, () => {
          // ...
        })
        .addCase(confirmVisit.rejected, (state) => {
          state.visit.confirming = false
          state.visit.error = true
          console.log('Error confirming visit!')
        });
      builder // cancel visit
        .addCase(cancelVisit.pending, (state) => {
          state.visit.canceling = true
        })
        .addCase(cancelVisit.fulfilled, (state) => {
          state.visit = {
            ...state.visit,
            canceling: false,
            cancelVisitModalOpen: false,
            status: visitStatusIds.canceled,
          }
        })
        .addCase(cancelVisit.rejected, () => {
          console.log('Error canceling visit!')
        });
    },
})

export const {
  setCalendarData,
  setCalendarFormData,
  setCalendarFormFocus,
  setPreLoadedCalendarFormData,
  setInitialCalendarData,
  setInitialVisitData,
  setKeepLoading,
} = CalendarSlice.actions

export default CalendarSlice.reducer
